import { useState, useEffect, useRef } from 'react';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { cn } from '../lib/utils';
import { turkishIncludes } from '../lib/turkishSearch';
import { getPreviewUrl, downloadDocument, revokePreviewUrl } from '../lib/documentPreview';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Eye,
  Search,
  Folder,
  FileImage,
  FileSpreadsheet,
  File,
  Calendar,
  User,
  Building2,
  Loader2,
  FolderOpen,
  IdCard,
  FileCheck2,
  Car,
  CheckCircle2,
  Upload,
  ZoomIn,
  RotateCw
} from 'lucide-react';
import DocumentPreviewModal from '../components/DocumentPreviewModal';

// File type icons
const getFileIcon = (fileType) => {
  const type = fileType?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(type)) {
    return <FileImage className="h-8 w-8 text-purple-500" />;
  }
  if (['xls', 'xlsx'].includes(type)) {
    return <FileSpreadsheet className="h-8 w-8 text-green-600" />;
  }
  if (['doc', 'docx'].includes(type)) {
    return <FileText className="h-8 w-8 text-blue-600" />;
  }
  if (type === 'pdf') {
    return <FileText className="h-8 w-8 text-red-500" />;
  }
  return <File className="h-8 w-8 text-slate-400" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Document type configurations
const DOC_CONFIGS = {
  identity_card: {
    icon: IdCard,
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-400',
    hoverBorder: 'hover:border-blue-500',
    label: 'Kimlik Kartı',
    description: 'Ön ve arka yüz',
    hasTwoSides: true,
    altTypes: ['identiteitskaart']
  },
  driver_license: {
    icon: Car,
    color: 'emerald',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-400',
    hoverBorder: 'hover:border-emerald-500',
    label: 'Ehliyet',
    description: 'Ön ve arka yüz',
    hasTwoSides: true,
    altTypes: ['rijbewijs']
  },
  passport: {
    icon: FileCheck2,
    color: 'purple',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-400',
    hoverBorder: 'hover:border-purple-500',
    label: 'Pasaport',
    description: 'Fotoğraflı sayfa',
    hasTwoSides: false,
    altTypes: ['paspoort']
  }
};

// Document Card with Two-Sided Support for MyDocuments
function DocumentCardUser({ docType, documents, userId, onRefresh, t }) {
  const config = DOC_CONFIGS[docType];
  const Icon = config.icon;
  
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  const [isDraggingFront, setIsDraggingFront] = useState(false);
  const [isDraggingBack, setIsDraggingBack] = useState(false);
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [frontImageUrl, setFrontImageUrl] = useState(null);
  const [backImageUrl, setBackImageUrl] = useState(null);
  const [frontIsPdf, setFrontIsPdf] = useState(false);
  const [backIsPdf, setBackIsPdf] = useState(false);
  
  // Find documents for this type
  const allTypeKeys = [docType, ...config.altTypes];
  const frontDoc = documents.find(d => 
    allTypeKeys.includes(d.document_type) && 
    (d.side === 'front' || !d.side)
  );
  const backDoc = documents.find(d => 
    allTypeKeys.includes(d.document_type) && 
    d.side === 'back'
  );
  
  // Load images with auth (skip for PDFs)
  useEffect(() => {
    const loadImage = async (doc, setUrl, setIsPdf) => {
      if (!doc) {
        setUrl(null);
        setIsPdf(false);
        return;
      }
      const isPdf = doc.file_type?.toLowerCase() === 'pdf' || doc.file_type?.toLowerCase() === 'application/pdf';
      setIsPdf(isPdf);
      if (isPdf) {
        setUrl(null);
        return;
      }
      try {
        // Şub 27, 2026 — Direct CDN URL ile hızlandır; fallback blob
        const { url: previewUrl } = await getPreviewUrl(api, doc.id, true);
        if (previewUrl) setUrl(previewUrl);
        else setUrl(null);
      } catch (error) {
        console.error('Error loading document image:', error);
        setUrl(null);
      }
    };
    
    loadImage(frontDoc, setFrontImageUrl, setFrontIsPdf);
    loadImage(backDoc, setBackImageUrl, setBackIsPdf);
    
    return () => {
      revokePreviewUrl(frontImageUrl);
      revokePreviewUrl(backImageUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontDoc?.id, backDoc?.id]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e, side) => {
    e.preventDefault();
    e.stopPropagation();
    if (side === 'front') setIsDraggingFront(true);
    else setIsDraggingBack(true);
  };

  const handleDragLeave = (e, side) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (side === 'front') setIsDraggingFront(false);
    else setIsDraggingBack(false);
  };

  const handleDrop = async (e, side) => {
    e.preventDefault();
    e.stopPropagation();
    if (side === 'front') setIsDraggingFront(false);
    else setIsDraggingBack(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await handleUpload(files[0], side);
    }
  };

  const handleFileSelect = async (e, side) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleUpload(file, side);
    }
    e.target.value = '';
  };

  const handleUpload = async (file, side) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('employeeProfile.unsupportedFileFormat') || 'Desteklenmeyen dosya formatı. JPG, PNG, PDF veya HEIC yükleyin.');
      return;
    }
    
    const setUploading = side === 'front' ? setUploadingFront : setUploadingBack;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('files', file);
      
      const sideLabel = side === 'front' ? (t('employeeProfile.frontSide') || 'Ön Yüz') : (t('employeeProfile.backSide') || 'Arka Yüz');
      formData.append('file_names', `${config.label} - ${sideLabel}`);
      formData.append('category', 'PERSONAL');
      formData.append('assigned_to', userId);
      formData.append('document_type', docType);
      formData.append('side', side);

      const response = await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(`${config.label} ${sideLabel} ${t('employeeProfile.documentSideUploaded') || 'yüklendi'}`);
        onRefresh();
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(t('employeeProfile.documentUploadError') || 'Belge yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    if (!doc) return;
    const ok = await downloadDocument(api, doc);
    if (!ok) toast.error(t('employeeProfile.downloadError') || 'İndirme hatası');
  };

  // Render upload zone
  const renderUploadZone = (side, isDragging, uploading, existingDoc, imageUrl) => {
    const isUploading = side === 'front' ? uploadingFront : uploadingBack;
    const inputRef = side === 'front' ? frontInputRef : backInputRef;
    const isPdf = side === 'front' ? frontIsPdf : backIsPdf;
    const sideLabel = side === 'front' ? (t('employeeProfile.frontSide') || 'Ön Yüz') : (t('employeeProfile.backSide') || 'Arka Yüz');
    
    return (
      <div 
        className={`
          relative rounded-lg overflow-hidden transition-all duration-200 cursor-pointer
          ${existingDoc && (imageUrl || isPdf)
            ? 'border-2 border-solid'
            : 'border-2 border-dashed'
          }
          ${isDragging 
            ? `${config.borderColor} bg-${config.color}-50 scale-[1.02]` 
            : existingDoc 
              ? 'border-green-300 bg-green-50/30' 
              : `border-slate-300 ${config.hoverBorder} hover:bg-slate-50`
          }
        `}
        style={{ aspectRatio: '1.6' }}
        onDragOver={handleDragOver}
        onDragEnter={(e) => handleDragEnter(e, side)}
        onDragLeave={(e) => handleDragLeave(e, side)}
        onDrop={(e) => handleDrop(e, side)}
        onClick={() => !existingDoc && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
          onChange={(e) => handleFileSelect(e, side)}
          data-testid={`doc-upload-user-${docType}-${side}`}
        />
        
        {existingDoc && isPdf ? (
          <div className="relative w-full h-full group flex flex-col items-center justify-center bg-red-50 dark:bg-red-900/20">
            <FileText className="h-10 w-10 text-red-500 mb-1" />
            <p className="text-xs font-semibold text-red-600">PDF</p>
            <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[90%] px-2">{existingDoc.original_name || existingDoc.file_name}</p>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <Button 
                size="sm" 
                variant="secondary"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); setPreviewDoc(existingDoc); }}
                data-testid={`doc-preview-user-${docType}-${side}`}
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="secondary"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); handleDownload(existingDoc); }}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
            <Badge className={`absolute top-2 left-2 ${config.bgColor} ${config.textColor} text-xs`}>
              {sideLabel}
            </Badge>
          </div>
        ) : existingDoc && imageUrl ? (
          <div className="relative w-full h-full group">
            <img 
              src={imageUrl} 
              alt={`${config.label} ${sideLabel}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <Button 
                size="sm" 
                variant="secondary"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); setPreviewDoc(existingDoc); }}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="secondary"
                className="h-8 px-2"
                onClick={(e) => { e.stopPropagation(); handleDownload(existingDoc); }}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
            <Badge className={`absolute top-2 left-2 ${config.bgColor} ${config.textColor} text-xs`}>
              {sideLabel}
            </Badge>
          </div>
        ) : isUploading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Loader2 className={`h-8 w-8 ${config.textColor} animate-spin mb-2`} />
            <p className="text-sm text-slate-500">Yükleniyor...</p>
          </div>
        ) : isDragging ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Upload className={`h-10 w-10 ${config.textColor} mb-2`} />
            <p className={`text-sm font-medium ${config.textColor}`}>Bırakın</p>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
            <div className={`h-10 w-10 rounded-lg ${config.bgColor} flex items-center justify-center mb-2`}>
              <Upload className={`h-5 w-5 ${config.textColor}`} />
            </div>
            <p className="text-xs font-medium text-slate-700 text-center">{sideLabel}</p>
            <p className="text-xs text-slate-400 text-center mt-1">Sürükle-bırak veya tıkla</p>
          </div>
        )}
      </div>
    );
  };

  const hasFront = !!frontDoc;
  const hasBack = !!backDoc;
  const isComplete = config.hasTwoSides ? (hasFront && hasBack) : hasFront;

  return (
    <>
      <div className={`bg-white dark:bg-slate-800 rounded-xl p-4 border-2 ${isComplete ? 'border-green-200 bg-green-50/30' : 'border-slate-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg ${config.bgColor} flex items-center justify-center`}>
              <Icon className={`h-5 w-5 ${config.textColor}`} />
            </div>
            <div>
              <p className="font-semibold text-slate-800 dark:text-slate-100">{config.label}</p>
              <p className="text-xs text-slate-500">{config.description}</p>
            </div>
          </div>
          {isComplete && (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Tamam
            </Badge>
          )}
        </div>

        {config.hasTwoSides ? (
          <div className="grid grid-cols-2 gap-3">
            {renderUploadZone('front', isDraggingFront, uploadingFront, frontDoc, frontImageUrl)}
            {renderUploadZone('back', isDraggingBack, uploadingBack, backDoc, backImageUrl)}
          </div>
        ) : (
          <div className="max-w-[200px] mx-auto">
            {renderUploadZone('front', isDraggingFront, uploadingFront, frontDoc, frontImageUrl)}
          </div>
        )}
      </div>

      <DocumentPreviewModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
      />
    </>
  );
}

// Important Documents Section Component
function ImportantDocumentsSection({ documents, userId, onRefresh, t }) {
  return (
    <Card className="border-2 border-amber-200 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2 text-amber-800 dark:text-amber-200">
          <IdCard className="h-5 w-5" />
          {t('employeeProfile.importantDocuments') || 'Önemli Belgeler'}
        </CardTitle>
        <CardDescription className="text-amber-700 dark:text-amber-300">
          {t('employeeProfile.importantDocumentsDesc') || 'Kimlik, Pasaport ve Ehliyet belgeleriniz (ön ve arka yüz)'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <DocumentCardUser 
            docType="identity_card"
            documents={documents}
            userId={userId}
            onRefresh={onRefresh}
            t={t}
          />
          <DocumentCardUser 
            docType="driver_license"
            documents={documents}
            userId={userId}
            onRefresh={onRefresh}
            t={t}
          />
          <DocumentCardUser 
            docType="passport"
            documents={documents}
            userId={userId}
            onRefresh={onRefresh}
            t={t}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export const MyDocuments = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    fetchDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/documents/my');
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error(t('documents.fetchError') || 'Dosyalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    const ok = await downloadDocument(api, doc);
    if (ok) toast.success(t('documents.downloadSuccess') || 'Dosya indiriliyor');
    else toast.error(t('documents.downloadError') || 'Dosya indirilemedi');
  };

  const handlePreview = async (doc) => {
    // Open images and PDFs in modal (like in profile page)
    const previewableTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const fileType = doc.file_type?.toLowerCase();
    
    if (previewableTypes.some(type => fileType?.includes(type) || type === fileType)) {
      // Open in modal
      setPreviewDoc(doc);
    } else if (['doc', 'docx', 'xls', 'xlsx'].includes(fileType)) {
      // Open DOC/XLS in new tab (not previewable in modal)
      try {
        const { url } = await getPreviewUrl(api, doc.id, true);
        if (url) window.open(url, '_blank');
      } catch (error) {
        console.error('Preview error:', error);
        toast.error(t('documents.previewError') || 'Önizleme açılamadı');
      }
    } else {
      // For other types, just download
      handleDownload(doc);
    }
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = !searchTerm || 
      turkishIncludes(doc.file_name || '', searchTerm) ||
      turkishIncludes(doc.original_name || '', searchTerm);
    
    const matchesTab = activeTab === 'all' || doc.category === activeTab.toUpperCase();
    
    return matchesSearch && matchesTab;
  });

  // Count by category
  const companyDocs = documents.filter(d => d.category === 'COMPANY').length;
  const personalDocs = documents.filter(d => d.category === 'PERSONAL').length;

  return (
    <div className="space-y-6" data-testid="my-documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('documents.myDocuments') || 'Dosyalarım'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('documents.myDocumentsDesc') || 'Şirket sözleşmeleri ve kişisel evraklarınız'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchDocuments} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.refresh') || 'Yenile'}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t('documents.searchPlaceholder') || 'Dosya ara...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Important Documents Section - With Front/Back Support */}
      <ImportantDocumentsSection 
        documents={documents}
        userId={user?.id}
        onRefresh={fetchDocuments}
        t={t}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            {t('documents.all') || 'Tümü'}
            <Badge variant="secondary" className="ml-1">{documents.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="company" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {t('documents.companyDocs') || 'Şirket Belgeleri'}
            <Badge variant="secondary" className="ml-1">{companyDocs}</Badge>
          </TabsTrigger>
          <TabsTrigger value="personal" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            {t('documents.personalDocs') || 'Kişisel Evraklar'}
            <Badge variant="secondary" className="ml-1">{personalDocs}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <Card className="p-12 text-center">
              <Folder className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">
                {t('documents.noDocuments') || 'Henüz dosya yok'}
              </h3>
              <p className="text-sm text-slate-400">
                {t('documents.noDocumentsDesc') || 'Size atanmış dosya bulunmuyor'}
              </p>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredDocuments.map((doc) => (
                <Card 
                  key={doc.id} 
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => handlePreview(doc)}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg group-hover:bg-slate-100 dark:bg-slate-700 transition-colors">
                      {getFileIcon(doc.file_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 dark:text-slate-100 truncate" title={doc.file_name}>
                        {doc.file_name}
                      </h3>
                      <p className="text-xs text-slate-400 truncate mt-0.5" title={doc.original_name}>
                        {doc.original_name}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 dark:text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(doc.created_at)}
                        </span>
                        <span>{formatFileSize(doc.file_size)}</span>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "mt-2 text-[10px]",
                          doc.category === 'COMPANY' 
                            ? "border-blue-200 text-blue-700 bg-blue-50" 
                            : "border-purple-200 text-purple-700 bg-purple-50"
                        )}
                      >
                        {doc.category === 'COMPANY' 
                          ? (t('documents.company') || 'Şirket') 
                          : (t('documents.personal') || 'Kişisel')}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 h-8 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreview(doc);
                      }}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      {t('documents.preview') || 'Görüntüle'}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 h-8 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(doc);
                      }}
                    >
                      <Download className="h-3.5 w-3.5 mr-1" />
                      {t('documents.download') || 'İndir'}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Document Preview Modal */}
      <DocumentPreviewModal 
        document={previewDoc}
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </div>
  );
};

export default MyDocuments;
